import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { CalendarIcon } from './Icons'

interface CustomDatePickerProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  buttonClassName?: string
  calendarClassName?: string
  square?: boolean
  placement?: 'down' | 'up'
  align?: 'start' | 'end'
  calendarWidth?: number
  /** ISO date (yyyy-mm-dd). Days after this date cannot be selected. */
  maxDate?: string
  /** ISO date (yyyy-mm-dd). Days before this date cannot be selected. */
  minDate?: string
  error?: boolean
  disabled?: boolean
  defaultYear?: number
}

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function toInputValue(date: Date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')

  return `${year}-${month}-${day}`
}

function toDisplayValue(value: string) {
  if (!value) {
    return ''
  }

  const [year, month, day] = value.split('-')

  if (!year || !month || !day) {
    return value
  }

  return `${year}-${month}-${day}`
}

interface CalendarDay {
  day: number
  isCurrentMonth: boolean
  dateObj: Date
}

function getCalendarDays(activeDate: Date): CalendarDay[] {
  const year = activeDate.getFullYear()
  const month = activeDate.getMonth()
  
  // First day of current month (0-6)
  const firstDay = new Date(year, month, 1)
  const leadingDaysCount = firstDay.getDay()
  
  // Total days in current month
  const totalDays = new Date(year, month + 1, 0).getDate()
  
  // Total days in previous month
  const prevMonthLastDate = new Date(year, month, 0).getDate()
  
  const days: CalendarDay[] = []
  
  // Previous month filler days
  for (let i = leadingDaysCount - 1; i >= 0; i--) {
    const d = prevMonthLastDate - i
    days.push({
      day: d,
      isCurrentMonth: false,
      dateObj: new Date(year, month - 1, d)
    })
  }
  
  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    days.push({
      day: i,
      isCurrentMonth: true,
      dateObj: new Date(year, month, i)
    })
  }
  
  // Next month filler days to complete the 6-row (42 cells) grid
  const remaining = 42 - days.length
  for (let i = 1; i <= remaining; i++) {
    days.push({
      day: i,
      isCurrentMonth: false,
      dateObj: new Date(year, month + 1, i)
    })
  }
  
  return days
}

function clampDay(year: number, month: number, day: number) {
  return Math.min(Math.max(day, 1), new Date(year, month + 1, 0).getDate())
}

function updateDateParts(current: Date, next: Partial<{ year: number; month: number; day: number }>) {
  const year = next.year ?? current.getFullYear()
  const month = next.month ?? current.getMonth()
  const day = clampDay(year, month, next.day ?? current.getDate())

  return new Date(year, month, day)
}

export function CustomDatePicker({
  label,
  value,
  onChange,
  placeholder = 'Select date',
  className = '',
  buttonClassName = '',
  square = false,
  calendarClassName = '',
  placement = 'down',
  align = 'start',
  calendarWidth = 280,
  maxDate,
  minDate,
  error = false,
  disabled = false,
  defaultYear,
}: CustomDatePickerProps) {
  const [open, setOpen] = useState(false)
  const [calendarStyle, setCalendarStyle] = useState<CSSProperties>({})
  const maxDateObj = maxDate ? new Date(`${maxDate}T00:00:00`) : null
  const minDateObj = minDate ? new Date(`${minDate}T00:00:00`) : null

  const minParts = useMemo(() => {
    if (!minDate) return null
    const [y, m, d] = minDate.split('-').map(Number)
    return { year: y, month: (m || 1) - 1, day: d || 1 }
  }, [minDate])

  const maxParts = useMemo(() => {
    if (!maxDate) return null
    const [y, m, d] = maxDate.split('-').map(Number)
    return { year: y, month: (m || 1) - 1, day: d || 1 }
  }, [maxDate])

  const getInitialActiveDate = () => {
    if (value) {
      const d = new Date(`${value}T00:00:00`)
      if (!Number.isNaN(d.getTime())) return d
    }
    const today = new Date()
    if (minDateObj && today < minDateObj) {
      return minDateObj
    }
    if (maxDateObj && today > maxDateObj) {
      return maxDateObj
    }
    if (defaultYear && Number.isFinite(defaultYear)) {
      return new Date(defaultYear, today.getMonth(), 1)
    }
    return today
  }

  const [activeDate, setActiveDate] = useState(getInitialActiveDate)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const calendarRef = useRef<HTMLDivElement | null>(null)
  const calendarDays = useMemo(() => getCalendarDays(activeDate), [activeDate])

  useEffect(() => {
    if (value) {
      const d = new Date(`${value}T00:00:00`)
      if (!Number.isNaN(d.getTime())) {
        setActiveDate(d)
        return
      }
    }
    const today = new Date()
    if (minDateObj && today < minDateObj) {
      setActiveDate(minDateObj)
    } else if (maxDateObj && today > maxDateObj) {
      setActiveDate(maxDateObj)
    } else if (defaultYear && Number.isFinite(defaultYear)) {
      setActiveDate(new Date(defaultYear, today.getMonth(), 1))
    } else {
      setActiveDate(today)
    }
  }, [value, maxDate, minDate, defaultYear])

  const updateCalendarPosition = () => {
    const rect = rootRef.current?.getBoundingClientRect()
    const width = Math.min(calendarWidth, window.innerWidth - 24)
    const maxHeight = Math.min(410, window.innerHeight - 24)

    if (!rect) {
      setCalendarStyle({
        left: '50%',
        top: '50%',
        width,
        maxHeight,
        transform: 'translate(-50%, -50%)',
      })
      return
    }

    const gap = 8
    const sidePadding = 12
    const preferredTop = placement === 'up' ? rect.top - gap - maxHeight : rect.bottom + gap
    const fallbackTop = placement === 'up' ? rect.bottom + gap : rect.top - gap - maxHeight
    const hasRoomInPlacement =
      placement === 'up'
        ? preferredTop >= sidePadding
        : preferredTop + maxHeight <= window.innerHeight - sidePadding
    const top = Math.max(sidePadding, Math.min(hasRoomInPlacement ? preferredTop : fallbackTop, window.innerHeight - maxHeight - sidePadding))
    const preferredLeft = align === 'end' ? rect.right - width : rect.left
    const left = Math.max(sidePadding, Math.min(preferredLeft, window.innerWidth - width - sidePadding))

    setCalendarStyle({
      left,
      top,
      width,
      maxHeight,
    })
  }

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node

      if (!rootRef.current?.contains(target) && !calendarRef.current?.contains(target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return
    }

    updateCalendarPosition()
    window.addEventListener('resize', updateCalendarPosition)
    window.addEventListener('scroll', updateCalendarPosition, true)

    return () => {
      window.removeEventListener('resize', updateCalendarPosition)
      window.removeEventListener('scroll', updateCalendarPosition, true)
    }
  }, [align, calendarWidth, open, placement])

  const selectedDate = value ? new Date(`${value}T00:00:00`) : null

  const isNextMonthDisabled = maxParts
    ? activeDate.getFullYear() > maxParts.year ||
      (activeDate.getFullYear() === maxParts.year && activeDate.getMonth() >= maxParts.month)
    : false

  const isPrevMonthDisabled = minParts
    ? activeDate.getFullYear() < minParts.year ||
      (activeDate.getFullYear() === minParts.year && activeDate.getMonth() <= minParts.month)
    : false

  const moveMonth = (direction: number) => {
    setActiveDate((current) => {
      const nextMonth = current.getMonth() + direction
      return updateDateParts(current, { month: nextMonth })
    })
  }

  const changeMonth = (month: number) => {
    setActiveDate((current) => {
      const year = current.getFullYear()
      if (minParts && (year < minParts.year || (year === minParts.year && month < minParts.month))) {
        return current
      }
      if (maxParts && (year > maxParts.year || (year === maxParts.year && month > maxParts.month))) {
        return current
      }
      return updateDateParts(current, { month })
    })
  }

  const changeYear = (yearValue: string) => {
    const year = Number(yearValue)
    if (!Number.isFinite(year)) {
      return
    }
    setActiveDate((current) => {
      let nextMonth = current.getMonth()
      if (minParts && year === minParts.year && nextMonth < minParts.month) {
        nextMonth = minParts.month
      }
      if (maxParts && year === maxParts.year && nextMonth > maxParts.month) {
        nextMonth = maxParts.month
      }
      return updateDateParts(current, { year, month: nextMonth })
    })
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation()
    onChange('')
    setOpen(false)
  }

  const selectDateObj = (dateObj: Date) => {
    onChange(toInputValue(dateObj))
    setOpen(false)
  }

  // Generate Year Options
  const currentYear = new Date().getFullYear()
  const yearOptions = useMemo(() => {
    const minYear = minParts ? minParts.year : currentYear - 80
    const maxYear = maxParts ? maxParts.year : currentYear + 10
    const years: number[] = []
    for (let i = maxYear; i >= minYear; i--) {
      years.push(i)
    }
    return years
  }, [currentYear, minParts, maxParts])

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {label ? <span className="mb-2 block text-[13px] font-semibold text-[#64748b]">{label}</span> : null}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((current) => !current)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            setOpen((current) => !current)
          }
        }}
        className={`relative flex ${square ? 'h-11 rounded-none text-[14px]' : 'h-10 rounded-[8px] text-[14px]'} w-full cursor-pointer items-center border ${
          error
            ? '!border-[#d92d20] !bg-[#fffbfa] text-[#101828] focus:!border-[#d92d20] focus:!ring-4 focus:!ring-[#d92d20]/10'
            : 'border-[#dfe7f0] bg-white text-[#111827] focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10 hover:border-[#146971]/60'
        } px-4 pr-16 text-left font-normal outline-none transition disabled:bg-[#f9fafb] disabled:text-[#667085] disabled:border-[#e4e7ec] disabled:cursor-not-allowed ${buttonClassName}`}
      >
        <span className={`min-w-0 flex-1 truncate text-left ${value ? (disabled ? 'text-[#667085]' : 'text-[#111827]') : 'text-[#9aa3af]'}`}>
          {value ? toDisplayValue(value) : placeholder}
        </span>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          {value && !disabled ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                handleClear(e)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  handleClear(e as any)
                }
              }}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#7c8aa3] hover:bg-slate-100 hover:text-gray-700 cursor-pointer transition-colors"
              aria-label="Clear date"
            >
              <X className="h-4 w-4 shrink-0" />
            </span>
          ) : null}
          <CalendarIcon className="pointer-events-none h-4 w-4 shrink-0 text-[#7c8aa3]" />
        </div>
      </div>

      {open && !disabled ? createPortal(
        <div
          ref={calendarRef}
          style={calendarStyle}
          className={`fixed z-[200] overflow-auto rounded-xl border border-[#dfe7f0] bg-white p-3.5 shadow-xl shadow-slate-900/10 ${calendarClassName}`}
        >
          {/* Calendar Header: Figma-styled Inline Dropdowns for Month and Year on the left, Chevrons on the right */}
          <div className="mb-3.5 flex items-center justify-between border-b border-[#f1f5f9] pb-2.5">
            <div className="flex items-center gap-1.5 pl-1">
              {/* Styled borderless Month Select */}
              <select
                value={activeDate.getMonth()}
                onChange={(event) => changeMonth(Number(event.target.value))}
                className="cursor-pointer bg-transparent text-[14px] font-bold text-slate-800 outline-none hover:text-[#146971]"
              >
                {monthNames.map((month, idx) => {
                  const isMonthDisabled =
                    (minParts && activeDate.getFullYear() === minParts.year && idx < minParts.month) ||
                    (maxParts && activeDate.getFullYear() === maxParts.year && idx > maxParts.month) ||
                    (minParts && activeDate.getFullYear() < minParts.year) ||
                    (maxParts && activeDate.getFullYear() > maxParts.year)

                  return (
                    <option key={month} value={idx} disabled={Boolean(isMonthDisabled)}>
                      {month}
                    </option>
                  )
                })}
              </select>

              {/* Styled borderless Year Select */}
              <select
                value={activeDate.getFullYear()}
                onChange={(event) => changeYear(event.target.value)}
                className="cursor-pointer bg-transparent text-[14px] font-bold text-slate-800 outline-none hover:text-[#146971]"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Navigation Chevrons */}
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => moveMonth(-1)}
                disabled={isPrevMonthDisabled}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                  isPrevMonthDisabled
                    ? 'cursor-not-allowed text-[#cbd3e0]'
                    : 'text-[#64748b] hover:bg-[#f2faf9] hover:text-[#146971]'
                }`}
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => moveMonth(1)}
                disabled={isNextMonthDisabled}
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition ${
                  isNextMonthDisabled
                    ? 'cursor-not-allowed text-[#cbd3e0]'
                    : 'text-[#64748b] hover:bg-[#f2faf9] hover:text-[#146971]'
                }`}
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Weekday Headers */}
          <div className="mb-2 grid grid-cols-7 text-center text-[10px] font-semibold text-[#94a3b8]">
            {weekDays.map((day, idx) => (
              <span key={`${day}-${idx}`} className="w-8">{day}</span>
            ))}
          </div>

          {/* Days Grid (Perfect Circle layout matching Figma standard) */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((dayItem, index) => {
              const isSelected =
                selectedDate?.getFullYear() === dayItem.dateObj.getFullYear() &&
                selectedDate.getMonth() === dayItem.dateObj.getMonth() &&
                selectedDate.getDate() === dayItem.dateObj.getDate()

              const itemDateStr = toInputValue(dayItem.dateObj)
              const isDisabled =
                Boolean(maxDate && itemDateStr > maxDate) ||
                Boolean(minDate && itemDateStr < minDate)

              return (
                <button
                  key={`${dayItem.day}-${index}`}
                  type="button"
                  onClick={() => !isDisabled && selectDateObj(dayItem.dateObj)}
                  disabled={isDisabled}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-medium transition ${
                    isDisabled
                      ? 'cursor-not-allowed text-slate-200'
                      : isSelected
                        ? 'bg-[#146971] text-white shadow-md shadow-[#146971]/20 font-semibold'
                        : dayItem.isCurrentMonth
                          ? 'text-[#334155] hover:bg-[#f2faf9] hover:text-[#146971]'
                          : 'text-[#cbd5e1] hover:bg-[#f2faf9] hover:text-[#146971]'
                  }`}
                >
                  {dayItem.day}
                </button>
              )
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-[#f1f5f9] pt-2">
            <button
              type="button"
              onClick={() => {
                const today = new Date()
                const todayStr = toInputValue(today)
                const isDisabled =
                  Boolean(maxDate && todayStr > maxDate) ||
                  Boolean(minDate && todayStr < minDate)
                if (!isDisabled) {
                  onChange(todayStr)
                  setOpen(false)
                }
              }}
              disabled={
                Boolean(maxDate && toInputValue(new Date()) > maxDate) ||
                Boolean(minDate && toInputValue(new Date()) < minDate)
              }
              className={`text-[12px] font-bold transition ${
                Boolean(maxDate && toInputValue(new Date()) > maxDate) ||
                Boolean(minDate && toInputValue(new Date()) < minDate)
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-[#146971] hover:text-[#0b484e]'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
              className="text-[12px] font-bold text-slate-500 transition hover:text-[#d92c2c]"
            >
              Clear
            </button>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

export default CustomDatePicker

